import type Phaser from "phaser";
import { silhouetteColour, silhouetteShows, type SilhouetteRule } from "odyssey-shared";
import type { CharacterFigure } from "./character.ts";

/**
 * The local character's silhouette through a building part (the author's ruling of 2026-10-03, ADR-040's amendment of
 * that day): building parts never fade, so while one hides the local character a copy of its shape draws above every
 * prop, filled with one flat colour at partial alpha, and the player never loses themselves. The copy is drawn from
 * the texture and frame the figure names as its shape (`CharacterFigure.shapeKey` and `shapeFrame`), with the same
 * pivot and the same feet point, raised by a hop's lift as the figure is, set each frame after the figure is posed, so it
 * animates with it. A player's shape is the page and the frame its own sprite shows (ADR-094's amendment of
 * 2026-10-07): the look it wears, hair or none, clothes and the tools composed into it, and the fallback look in any
 * frame the character itself is drawn in that.
 * Which share of the body shows it is still measured on one body box for every look. It does not pulse or move by
 * itself, so reduced motion asks nothing of it.
 *
 * One image is made on first use and reused, shown or hidden, for the scene's life; a frame allocates nothing. The
 * image is not masked to the covering part: a mask over a part's image is rendered again into a texture each frame,
 * which costs more than the slice of the body it would spare at the share the silhouette shows from. A figure drawn
 * from layers (`CharacterSprite`) names its bottom layer alone.
 */

/**
 * The depth the silhouette draws at: above every prop, roof and character of the world, under the debug overlay.
 */
export const SILHOUETTE_DEPTH = 900_000;

/**
 * The silhouette of one scene's local character.
 */
export class Silhouette {
  private image: Phaser.GameObjects.Image | null = null;
  private shown = false;
  private strip = "";
  private frame = -1;

  /**
   * Draws nothing until the first `sync` that shows it.
   *
   * @param scene The scene the local character is drawn in.
   * @param rule The colour, alpha and share of the body that shows it.
   * @param fill The tint mode that replaces every pixel's colour with the tint, keeping its alpha
   *   (`Phaser.TintModes.FILL`), handed in so this module loads where Phaser cannot.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly rule: SilhouetteRule,
    private readonly fill: Phaser.TintModes,
  ) {}

  /**
   * Whether the silhouette is drawn now, for the report.
   *
   * @returns True while it shows.
   */
  public get visible(): boolean {
    return this.shown;
  }

  /**
   * Shows the silhouette over the character while a building part hides enough of its body, on the shape the figure
   * names now, and hides it otherwise.
   *
   * @param sprite The local character's sprite, posed for this frame.
   * @param keptCover The share of its body building parts hide together (`PropFade.keptCover`).
   */
  public sync(sprite: CharacterFigure, keptCover: number): void {
    if (!silhouetteShows(keptCover, this.rule)) {
      this.hide();
      return;
    }
    const strip = sprite.shapeKey;
    if (strip === "") {
      this.hide();
      return;
    }
    let image = this.image;
    if (image === null) {
      image = this.scene.add.image(0, 0, strip, sprite.shapeFrame).setDepth(SILHOUETTE_DEPTH).setTint(silhouetteColour(this.rule)).setTintMode(this.fill).setAlpha(this.rule.alpha);
      this.image = image;
    }
    const frame = sprite.shapeFrame;
    if (this.strip !== strip || this.frame !== frame) {
      image.setTexture(strip, frame);
      this.strip = strip;
      this.frame = frame;
    }
    image.setOrigin(sprite.originX, sprite.originY);
    image.setPosition(sprite.x, sprite.y - sprite.lift);
    if (!this.shown) {
      image.setVisible(true);
      this.shown = true;
    }
  }

  /**
   * Hides the silhouette, for a frame in which nothing hides the character or the character is gone.
   */
  public hide(): void {
    if (this.shown) {
      this.image?.setVisible(false);
      this.shown = false;
    }
  }
}
